import * as React from 'react';
import { styled } from '@glitz/react';
import { Block } from 'webapp/components/page-layout';
import LinkBase from 'webapp/components/Link';
import { ChevronRightIcon } from 'nordicnest/shared/icons';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { createFadeAnimationComponent } from '@avensia/excite-ui';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { useDynamicData } from '@avensia/excite';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { CloseIcon } from 'webapp/components/icons';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { getLocalStorageItem, setLocalStorageItem } from '@avensia/excite';

type NotificationVisibilityStatus = 'hidden' | 'visible';

export default function MiniCartNotification() {
  const [notification] = useDynamicData<NordicNest.Api.CartNotificationApiModel>();
  const [notificationStatus, setNotificationStatus] = React.useState<string>('');
  const [notificationKey, setNotificationKey] = React.useState<string>('');
  // const notificationKey = !notification?.content ? '' : `n_${notification.notificationHash}`;

  React.useEffect(() => {
    if (notification && notification.notificationHash) {
      setNotificationKey(`n_${notification.notificationHash}`);
    }

    if (notification && notificationKey.length) {
      const storedNotificationStatus = getLocalStorageItem<NotificationVisibilityStatus>(notificationKey) || 'visible';
      setNotificationStatus(storedNotificationStatus);
    }
  }, [notification, notificationKey]);

  if (!notificationKey.length) {
    return null;
  }

  const closeNotification = () => {
    setLocalStorageItem(notificationKey, 'hidden');
    setNotificationStatus('hidden');
  };

  return (
    <Base visible={notificationStatus === 'visible'}>
      <CloseButton as={Theme.Transparent} onClick={closeNotification}>
        <CloseIcon />
      </CloseButton>
      <Text>
        <Document elements={notification?.content?.elements} styleDecorators={styles} />
      </Text>
      {notification?.linkPath && notification?.linkLabel && (
        <MoreInfo to={notification.linkPath}>
          {notification.linkLabel} <ChevronRightIcon />
        </MoreInfo>
      )}
    </Base>
  );
}

const Text = styled.div({
  fontSize: t => t.fontSize.xxs,
  color: 'white',
  paddingRight: t => t.spacing.m,
});

const MoreInfo = styled(LinkBase, {
  color: t => t.secondaryLight,
  textTransform: 'uppercase',
  textDecoration: 0,
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  fontSize: t => t.fontSize.xxs,
  ':hover': {
    color: t => t.secondaryLight,
  },
  ':visited': {
    color: t => t.secondaryLight,
  },
});

const CloseButton = styled(Button, createButtonHoverDecorator({ color: t => t.secondaryLight }), {
  position: 'absolute',
  top: 10,
  right: 10,
  padding: { xy: 0 },
  margin: { xy: 0 },
  zIndex: 1,
  color: t => t.secondaryLight,
  ':visited': {
    color: t => t.secondaryLight,
  },
});

const Base = createFadeAnimationComponent(
  styled(Block, {
    backgroundColor: t => t.darkGrey,
    padding: { xy: t => t.sharedSpacing.m },
    marginTop: 17,
    position: 'relative',
    width: '100%',
    marginBottom: t => t.sharedSpacing.l,
  }),
);

const styles: StyleDecorators = {
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
  }),
};
